// src/components/CreatePersonnelDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { createPersonnel } from '../services/personnelService';

/**
 * Add a single personnel record to a command in the ACTIVE exercise.
 * For the "one missing officer" case where a full CSV upload isn't warranted.
 *
 * `commands` passed in should already be the ACTIVE exercise's commands.
 */
const EMPTY = {
  serviceNumber: '',
  ippisNumber: '',
  firstName: '',
  middleName: '',
  lastName: '',
  gender: '',
  birthDate: '',
  rankName: '',
  gradeName: '',
  gradeLevel: '',
  gradeStep: '',
  dateOfFirstAppointment: '',
  expectedRetirementDate: '',
  bankName: '',
  accountNumber: '',
  pfaName: '',
  pencomId: '',
  nhfNumber: '',
  phoneNumber: '',
  email: '',
  commandId: '',
};

const CreatePersonnelDialog = ({ open, commands, activeExerciseName, onClose, onSuccess, onNotify }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setError('');
    }
  }, [open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async () => {
    setError('');

    // Required fields (match schema NOT NULL)
    const required = [
      ['serviceNumber', 'Service Number'],
      ['ippisNumber', 'IPPIS Number'],
      ['firstName', 'First Name'],
      ['lastName', 'Last Name'],
      ['gender', 'Gender'],
      ['rankName', 'Rank'],
      ['gradeName', 'Grade Name'],
      ['gradeLevel', 'Grade Level'],
      ['gradeStep', 'Grade Step'],
      ['bankName', 'Bank Name'],
      ['accountNumber', 'Account Number'],
      ['pfaName', 'PFA Name'],
      ['pencomId', 'PENCOM ID'],
      ['commandId', 'Command'],
    ];
    for (const [key, label] of required) {
      if (!form[key] || !form[key].toString().trim()) {
        setError(`${label} is required`);
        return;
      }
    }

    setLoading(true);
    try {
      await createPersonnel({
        serviceNumber: form.serviceNumber.trim(),
        ippisNumber: form.ippisNumber.trim(),
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || null,
        lastName: form.lastName.trim(),
        gender: form.gender,
        birthDate: form.birthDate || null,
        rankName: form.rankName.trim(),
        gradeName: form.gradeName.trim(),
        gradeLevel: form.gradeLevel.trim(),
        gradeStep: form.gradeStep.trim(),
        dateOfFirstAppointment: form.dateOfFirstAppointment || null,
        expectedRetirementDate: form.expectedRetirementDate || null,
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        pfaName: form.pfaName.trim(),
        pencomId: form.pencomId.trim(),
        nhfNumber: form.nhfNumber.trim() || null,
        phoneNumber: form.phoneNumber.trim() || null,
        email: form.email.trim() || null,
        commandId: form.commandId,
      });
      const name = `${form.firstName} ${form.lastName}`.trim();
      if (onNotify) onNotify(`Personnel ${name} added successfully`, 'success');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to add personnel';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Personnel</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Adding a single officer to a command in the active exercise
            {activeExerciseName ? <> (<strong>{activeExerciseName}</strong>)</> : null}. For
            many records at once, use Upload CSV instead.
          </Typography>

          {(!commands || commands.length === 0) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              There are no commands in the active exercise to add personnel to.
              Make sure an exercise is active and has commands.
            </Alert>
          )}

          <Divider sx={{ mb: 2 }}>Identity</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Service Number" name="serviceNumber" value={form.serviceNumber} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="IPPIS Number" name="ippisNumber" value={form.ippisNumber} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField select label="Gender" name="gender" value={form.gender} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading}>
              <MenuItem value="MALE">Male</MenuItem>
              <MenuItem value="FEMALE">Female</MenuItem>
            </TextField>
            <TextField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Middle Name" name="middleName" value={form.middleName} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Date of Birth" name="birthDate" type="date" value={form.birthDate} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} InputLabelProps={{ shrink: true }} disabled={loading} />
          </Box>

          <Divider sx={{ my: 2 }}>Employment</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Rank" name="rankName" value={form.rankName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Grade Name" name="gradeName" value={form.gradeName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} placeholder="e.g. CONPASS" />
            <TextField label="Grade Level" name="gradeLevel" value={form.gradeLevel} onChange={handleChange} required size="small" sx={{ flex: '1 1 13%' }} disabled={loading} placeholder="e.g. 08" />
            <TextField label="Grade Step" name="gradeStep" value={form.gradeStep} onChange={handleChange} required size="small" sx={{ flex: '1 1 13%' }} disabled={loading} placeholder="e.g. 03" />
            <TextField label="Date of First Appointment" name="dateOfFirstAppointment" type="date" value={form.dateOfFirstAppointment} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} InputLabelProps={{ shrink: true }} disabled={loading} />
            <TextField label="Expected Retirement Date" name="expectedRetirementDate" type="date" value={form.expectedRetirementDate} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} InputLabelProps={{ shrink: true }} disabled={loading} />
            <TextField select label="Command" name="commandId" value={form.commandId} onChange={handleChange} required size="small" sx={{ flex: '1 1 100%' }} disabled={loading || !commands || commands.length === 0}>
              {(commands || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.code} - {c.name} ({c.zone})</MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider sx={{ my: 2 }}>Financial</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Bank Name" name="bankName" value={form.bankName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Account Number" name="accountNumber" value={form.accountNumber} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="PFA Name" name="pfaName" value={form.pfaName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="PENCOM ID" name="pencomId" value={form.pencomId} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="NHF Number" name="nhfNumber" value={form.nhfNumber} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
          </Box>

          <Divider sx={{ my: 2 }}>Contact (optional)</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Phone Number" name="phoneNumber" value={form.phoneNumber} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !commands || commands.length === 0}>
          {loading ? <CircularProgress size={24} /> : 'Add Personnel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePersonnelDialog;